import React, { PropsWithChildren, useCallback } from 'react';
import {
  DragDropContext,
  DragStart,
  DragUpdate,
  DropResult,
} from 'react-beautiful-dnd';
import {
  buildDraggingPlaceholderElement,
  findAllDraggingPlaceholderElements,
  findDraggingElement,
  findDroppableElement,
} from './helpers';
import { calculatePlaceHolderTop } from './helpers/calculatePlaceHolderTop';

export const RULEBUILDER_DROPPABLE_ID = 'RuleBuilder';
export const FIELDS_DROPPABLE_ID = 'Field';

interface RuleBuilderDragDropContextProps {
  onDragEnd: (result: DropResult) => void;
  ruleBuilderFieldsLength?: number;
}

function RuleBuilderDragDropContext({
  onDragEnd,
  children,
  ruleBuilderFieldsLength,
}: PropsWithChildren<RuleBuilderDragDropContextProps>): JSX.Element {
  const dragEndHandler = useCallback(
    (result: DropResult): void => {
      // remove all placeholders
      findAllDraggingPlaceholderElements().forEach((elem) => elem.remove());
      onDragEnd(result);
    },
    [onDragEnd],
  );

  const dragStartHandler = useCallback((event: DragStart): void => {
    const draggableId = event.draggableId;
    const draggingElement = findDraggingElement(draggableId);
    if (!draggingElement) return;

    // build dragging placeholder
    const droppableElement = draggingElement.parentElement;
    const placeholderElement = buildDraggingPlaceholderElement(
      droppableElement,
      draggingElement,
      draggableId,
    );

    // reposition placeholder
    const sourceIndex = event.source.index;
    placeholderElement.style.top = calculatePlaceHolderTop(
      droppableElement,
      sourceIndex,
      sourceIndex,
    );
  }, []);

  const calculateIndex = useCallback((event: DragUpdate): number => {
    const { destination } = event;
    return destination ? destination.index : -1;
  }, []);

  const dragUpdateHandler = useCallback(
    (event: DragUpdate): void => {
      const { draggableId, source, destination } = event;

      // hide all placeholders
      findAllDraggingPlaceholderElements().forEach((elem) => {
        elem.style.top = '-10000px';
      });

      const draggingElement = findDraggingElement(draggableId);
      if (!draggingElement) return;
      const droppableElement = findDroppableElement(destination?.droppableId);
      if (!droppableElement) return;

      const placeholderElement = buildDraggingPlaceholderElement(
        droppableElement,
        draggingElement,
        draggableId,
      );
      if (!placeholderElement) return;

      // reposition dragging placeholder
      const dropIndex = calculateIndex(event);
      // if last field in rule builder, make placeholder/dropzone larger
      if (dropIndex === ruleBuilderFieldsLength) {
        placeholderElement.style.height = '80px';
      } else {
        placeholderElement.style.height = draggingElement.style.height;
      }

      const sourceIndex =
        source.droppableId === destination.droppableId
          ? source.index
          : Infinity;
      placeholderElement.style.top = calculatePlaceHolderTop(
        droppableElement,
        dropIndex,
        sourceIndex,
      );
    },
    [ruleBuilderFieldsLength, calculateIndex],
  );

  return (
    <DragDropContext
      onDragEnd={dragEndHandler}
      onDragStart={dragStartHandler}
      onDragUpdate={dragUpdateHandler}
    >
      {children}
    </DragDropContext>
  );
}

export default RuleBuilderDragDropContext;
